import { PropsWithChildren, useLayoutEffect, useState } from 'react';

import { ShippingTypesCodes } from '@/data/libraries/ShippingTypes';
import { store } from '@/data/libraries/Store';
import { Col, Container, Row, Image } from 'react-bootstrap';

import { useAppSelector } from '@/ui/store/helperRedux';
import { RootState } from '@/ui/store/store';

import CartController from '@/controllers/CartController';
import BranchOfficesController from '@/controllers/BranchOfficesController';

import { IShippingTypes } from '@/domain/interfaces/IProduct';
import { ICart } from '@/domain/interfaces/ICart';
import { IBranchOffice } from '@/domain/interfaces/IBranchOffice';
import { IStore } from '@/domain/interfaces/IStore';
import { translate } from '@/domain/utils/translate';
import ErrorImage from '@/ui/assets/ErrorImage';

import MailingAddress from '@/ui/components/sales/MailingAddress';
import SelectButton from '@/ui/components/SelectButton';
import CheckoutAlertModal from '@/ui/components/modals/ChekoutAlertModal';


interface ShippingTypeLayoutProps {
  /** Objeto tipo useRef que almacenará el formulario de compra */
  formRef: React.RefObject<ICart>;
  /** Función para actualizar el atributo formRef  */
  updateFormRef: (newFormRef: ICart) => void;
  /** Función para el botón del siguiente paso */
  handleNextButton: (e: MouseEvent) => void;
}

/** Tipo para representar las diferentes vistas del componente */
type ViewType = 'none' | 'mailingAddress' | 'shippingOptions';

/**
 * Componente layout para tipos de entrega
 * @component
 */
const ShippingTypeLayout = ({ formRef, updateFormRef, handleNextButton }: PropsWithChildren<ShippingTypeLayoutProps>): JSX.Element => {

  /** Estado para controlar qué vista se muestra */
  const [currentView, setCurrentView] = useState<ViewType>('none');

  /** Estado Redux Tienda */
  const storeData = useAppSelector((state: RootState) => state.storeData) as IStore | null;

  /** Variable de entorno - Url de imágenes */
  const URL_IMAGES: string = process.env.REACT_APP_URL_IMAGES ?? '';

  /** Controlador de sucursales */
  const { getBranchOffices } = BranchOfficesController();

  /** Controlador de carrito */
  const { updateClientInCart } = CartController();

  /** Controlador de carrito */
  const { updateShippingTypeCost } = CartController();

  /**Trae la información del carrito desde Redux */
  const cartProducts = useAppSelector((state: RootState) => state.cartState) as ICart;

  /**Trae la información del tipos de entrega desde Redux */
  const shippingTypesState: IShippingTypes[] = useAppSelector((state: RootState) => state.shippingTypes as IShippingTypes[]);

  /** Valor del ID de entrega a domicilio */
  const homeDeliveryId: number = ShippingTypesCodes.NATIONAL_SHIPMENT;

  /** Valor del ID de entrega a domicilio */
  const pickUpId: number = ShippingTypesCodes.PICK_UP;

  /** Valor del ID de la tienda */
  const storeId: number = store.DEFAULT;

  /** Objeto inicial del carrito */
  const formCartCheckOut: ICart = formRef.current ? JSON.parse(JSON.stringify(formRef.current)) : JSON.parse(JSON.stringify(cartProducts));

  /** Limpia los campos de dirección */
  const clearAddressFields = (): void => {
    if (formCartCheckOut.clientDetails?.address) {
      formCartCheckOut.clientDetails.address = '';
      formCartCheckOut.clientDetails.region = '';
      formCartCheckOut.clientDetails.optionalAddress = '';
      updateFormRef(formCartCheckOut);
      updateClientInCart(formCartCheckOut.clientDetails);
    }
  };

  /** Guarda dirección de sucursal */
  const savePickUpAddress = async (): Promise<void> => {

    updateShippingTypeCost(0);
    clearAddressFields();
    let branchOfficeData: string = ENABLED_OFFICE;

    const branchOfficesResponse: IBranchOffice[] = await getBranchOffices(storeId);

    if (branchOfficesResponse) {
      if (branchOfficesResponse.length > 0) {
        branchOfficeData = branchOfficesResponse[0].contact.address;
      }
      formCartCheckOut.clientDetails.shippingType = shippingTypesState.find((shippingType) => shippingType.id === pickUpId) ?? null;
      formCartCheckOut.clientDetails.pickupAddress = branchOfficeData;

      updateFormRef(formCartCheckOut);
      updateClientInCart(formCartCheckOut.clientDetails);
    }

    const event = new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
    });
    handleNextButton(event);
  };

  const setShippingCostFromStoreData = () => {
    updateShippingTypeCost(parseInt(storeData?.shippingCost ?? '0'));
  };

  /** Habilita el componente de dirección de entrega */
  const showAddressComponent = (): void => {
    if (storeData?.name && storeData.name.length > 0) {
      setShippingCostFromStoreData();
    }

    // Cambiar a la vista de dirección de correo
    setCurrentView('mailingAddress');
  };

  /** Oculta el componente de dirección de entrega */
  const hideAddressComponent = (): void => {
    clearAddressFields();
    setCurrentView('shippingOptions');
  };

  /** Establece si se debe habilitar o no la opción de tipo de entrega */
  const checkCartProductsShippingTypes = (shippingId: number): boolean => {
    const hasAValidSippingType: boolean | undefined = formRef.current?.details.every((product) => {
      return product.shippingTypes.some((shippingType) => shippingType.id === shippingId);
    });

    if (hasAValidSippingType !== undefined && hasAValidSippingType === true) {
      return true;
    } else {
      return false;
    }
  };

  /**
   * Devuelve lista para selección de tipo de entrega
   * @component
   */
  const ShippingTypeOptions = (): JSX.Element => {
    return (
      <>
        {shippingTypesState.length > 1 && shippingTypesState.map((shippingType, index) => {
          const disabled: boolean = !checkCartProductsShippingTypes(shippingType.id);

          return (
            <SelectButton
              key={`shippingType-${index + 1}`}
              icon={shippingType?.icon ?? ''}
              label={shippingType?.title ?? ''}
              secondaryLabel={shippingType.description ?? shippingType.title}
              disabled={disabled}
              actionButton={shippingType.id === homeDeliveryId ? showAddressComponent : savePickUpAddress}
            />
          );
        })}
      </>
    );
  };

  /** Determina qué vista mostrar según los tipos de envío disponibles */
  const determineViewToShow = (): void => {
    if (shippingTypesState.length === 1 && shippingTypesState[0].id === ShippingTypesCodes.PICK_UP) {
      /** Si es solo retiro en tienda se asume que el comportamiento lo determinó CartCheckOut */
      setCurrentView('none');
    } else if (shippingTypesState.length === 1 && shippingTypesState[0].id === ShippingTypesCodes.NATIONAL_SHIPMENT) {
      /** Si es solo entrega a domicilio se muestra componente dirección */
      setCurrentView('mailingAddress');
      setShippingCostFromStoreData();
    } else {
      /** Evita renderizar el componente de selección de tipo de entrega si aún no se determina cuales están habilitados */
      if (shippingTypesState.length > 0) {
        setCurrentView('shippingOptions');
      }
    }
  };



  /** Establece la vista inicial del componente basada en los tipos de envío */
  useLayoutEffect((): void => {
    determineViewToShow();
  }, [shippingTypesState]);



  /** Imágenes */
  const SHIPPING_TYPE_IMAGE: string = `${URL_IMAGES}/client/shipping-type.png`;

  /** Textos de componente */
  const TITLE_TEXT = translate('checkout.shipment_title_text');
  const ENABLED_OFFICE = translate('checkout.enabled_office');
  const DESCRIPTION_TEXT = translate('checkout.description_text');
  const ALERT_MESSAGE = translate('checkout.alert_modal_text');
  /** Renderizado condicional basado en la vista actual */
  return (
    <>
      {currentView === 'mailingAddress' && (
        <MailingAddress
          formRef={formRef}
          updateFormRef={updateFormRef}
          returnAction={hideAddressComponent}
          handleNextButton={(e: MouseEvent) => handleNextButton(e)}
        />
      )}

      {currentView === 'shippingOptions' && (
        <Container fluid className='p-0'>
          <Row className='g-0 mt-4 py-3 px-5 bg-primary-4 rounded rounded-4 shadow'>
            <Col lg={5} className='mx-auto my-4'>
              <Col lg={12} className='pb-2'>
                <Image
                  src={SHIPPING_TYPE_IMAGE}
                  width={200}
                  alt={TITLE_TEXT}
                  onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                    e.currentTarget.src = ErrorImage;
                  }}
                />
                <h2 className='pt-2'>{TITLE_TEXT}</h2>
              </Col>
              <Col lg={12} className='text-secondary-2 text-start'>
                <p>{DESCRIPTION_TEXT}</p>
              </Col>

              {/* Mostrar alerta solo cuando la opción de envío a domicilio está habilitada (no deshabilitada) */}
              {storeData?.onlyChileanRegion &&
                shippingTypesState.some(shippingType => shippingType.id === ShippingTypesCodes.NATIONAL_SHIPMENT) &&
                checkCartProductsShippingTypes(ShippingTypesCodes.NATIONAL_SHIPMENT) && (
                  <Col lg={12} className='pb-3'>
                    <CheckoutAlertModal message={ALERT_MESSAGE} type={'warning'} />
                  </Col>
                )}

              <Col lg={12} className=''>
                <ShippingTypeOptions />
              </Col>
            </Col>
          </Row>
        </Container >
      )}
    </>
  );
};

export default ShippingTypeLayout;
